import { useLayoutEffect, useRef, useState } from 'react'

import { Tooltip, chakra } from '@enechain/ecloud-designsystem'

type Props = {
  /**
   * hover で出す説明。 `undefined` / `null` / 空文字なら Tooltip を開かせない
   * (= 「押せない理由がある時だけ出す」 形をそのまま書ける)。
   */
  content: React.ReactNode
  children: React.ReactNode
  /**
   * 説明がある間、 包みを Tab の止まり先にする。 子が focus を持たない要素 (バッジ・文字など) の
   * 時だけ渡す (design/ui-feasibility/components/action-reason.md の区分 2)。
   *
   * focus を受ける子には付けない (同じ操作に Tab が 2 回止まる)。 DS の `PrimaryButton` /
   * `SecondaryButton` / `TertiaryButton` は `disabled` でも `aria-disabled` で表され focus を
   * 受けたままなので、 ここに含む。 native disabled で focus を受けられない子は渡さなくても
   * 包みが止まり先になる (下の JSDoc「包みが Tab の止まり先になる条件」)。
   */
  focusable?: boolean
  /** Tooltip に矢印を付ける。 */
  showArrow?: boolean
}

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled):not([type="hidden"])',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ')

/**
 * 説明が無い間の Tooltip。 開かせず、 吹き出しの中身も描かない。 説明がある間は何も渡さず
 * DS の既定 (hover / focus で開くまで中身を描かない) のままにする。
 */
const CLOSED_WHILE_EMPTY = {
  open: false,
  lazyMount: true,
  unmountOnExit: true,
} as const

/**
 * 説明が無い間、 hover を Tooltip に渡さない。 zag の Tooltip は、 ほかの吹き出しが出ている間の
 * hover (pointer.move) では待たずに開き、 その経路だけは `open={false}` を見ない
 * (@zag-js/tooltip 1.41 の closed 状態の pointer.move)。 trigger の handler は
 * `event.defaultPrevented` なら何もしないので、 bubble の前の capture で止める。
 * focus の経路は `open={false}` を見るので止めなくてよい。
 */
const stopTooltipHover = (event: React.PointerEvent): void => {
  event.preventDefault()
}
const HOVER_BLOCKED_WHILE_EMPTY = {
  onPointerMoveCapture: stopTooltipHover,
  onPointerOverCapture: stopTooltipHover,
}

/**
 * 一部の選択肢だけ disabled の group (radio 等) は残りで focus を受けるので、 `:disabled` が
 * あっても止まれる要素が残る時は除く。
 */
const cannotTakeFocus = (wrapper: HTMLElement): boolean =>
  wrapper.querySelector(':disabled') !== null &&
  wrapper.querySelector(TABBABLE_SELECTOR) === null

/**
 * ボタンの名前・押せない理由を **DS `Tooltip`** で出す共通ラッパー。
 *
 * デザイナー既定 (2026-08-17、 dev 全体の共通ルール):
 *
 * > アイコンのみのボタンは hover でボタン名を出す。 native `title` 属性は表示まで
 * > 約 1 秒かかり見た目も OS 依存なので、 DS Tooltip で出す。 `title` は Tooltip
 * > 非対応の環境 (タッチ・支援技術の一部) 向けの保険として残す。
 *
 * つまり **DS `Tooltip` が主役 + `title` は保険**。 呼び出し側は `title` を
 * 消さずに、 本 component で包む。
 *
 * ## なぜ `<span>` で包むのか
 *
 * `disabled` なボタンでも hover は拾えるが (Chromium 実測)、 DS `Tooltip` は
 * child に handler を付けて動くため、 **無効化されて操作を受け付けない要素だと
 * 取りこぼす**ことがある。 同じ理由で `Supply48Data/components/Toolbar.tsx` が
 * 既に `<span style={{ display: 'inline-flex' }}>` を挟む形を採っており、
 * その書き方をここに一本化する。
 *
 * `inline-flex` は元のボタンの寸法・並びを変えないための指定
 * (`inline` だと行ボックスに乗って高さが変わる)。
 *
 * ## 説明が無い間も Tooltip は置いたまま閉じておく
 *
 * DS `Tooltip` の `isHidden` / `disabled` は、 切り替えると Tooltip を外して子をそのまま返す
 * (`disabled` は子ごと描かない)。 間の要素が増減するので**中のボタンが作り直され**、
 * focus 中のボタンから focus が外れる。 押した瞬間に「保存中」 の理由が出るボタンでは、
 * キーボードで押した人の focus が body に落ちる (#10834 の指摘)。
 *
 * そのため Tooltip は常に置き、 説明が無い間だけ `open={false}` で開かせない
 * (`CLOSED_WHILE_EMPTY`)。 吹き出しの中身も描かないので、 空の吹き出しは出ない。
 * ほかの吹き出しが出ている時の hover は `open={false}` を見ずに開くので、 包みで止める
 * (`HOVER_BLOCKED_WHILE_EMPTY`)。
 *
 * ## 包みが Tab の止まり先になる条件
 *
 * 説明があり、 次のどちらかの時だけ包みに `tabIndex={0}` を付ける (UI 辞書
 * components/action-reason.md 区分 2「包みに tabindex="0" を付ける」)。 それ以外は子が自分で
 * focus を受け、 focus が包みへ伝わって説明が開く (Tab は 1 回だけ止まる)。
 *
 * - 子が native disabled で focus を受けられない: DS の `IconButton` / `Switch` / `Checkbox` /
 *   `NativeSelect`、 `loading` 中の DS ボタン、 native の select / input / button。
 *   DS のボタンは disabled を native で持つ部品 (`IconButton`) と `aria-disabled` で持ち focus を
 *   受けたままの部品 (`PrimaryButton` 等) があり、 props からは見分けられないので、 描画後の
 *   DOM の `:disabled` で判定する
 * - `focusable` を渡した (子が focus を持たない要素)
 */
export const ButtonTooltip = ({
  content,
  children,
  focusable = false,
  showArrow,
}: Props): React.ReactElement => {
  const isHidden = content === undefined || content === null || content === ''
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const [isChildUnfocusable, setIsChildUnfocusable] = useState(false)
  // 子の disabled は説明と同時に変わることが多いので、 説明の有無も測り直しの契機にする
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    setIsChildUnfocusable(wrapper !== null && cannotTakeFocus(wrapper))
  }, [children, isHidden])
  return (
    <Tooltip
      content={content ?? ''}
      showArrow={showArrow}
      {...(isHidden ? CLOSED_WHILE_EMPTY : {})}
    >
      <chakra.span
        ref={wrapperRef}
        display="inline-flex"
        tabIndex={
          !isHidden && (focusable || isChildUnfocusable) ? 0 : undefined
        }
        {...(isHidden ? HOVER_BLOCKED_WHILE_EMPTY : {})}
      >
        {children}
      </chakra.span>
    </Tooltip>
  )
}
